import type { LegalContent } from './legal.ts';

// Legal texts — English. A faithful translation of `legal-tr.ts`: every
// statement describes actual behaviour of the software. The Turkish text is
// the reference version; in case of discrepancy the Turkish version applies.

export const legalEn: LegalContent = {
  title: 'Legal',
  updated: 'Last updated: 13 October 2026',
  note: 'These texts describe what the software actually does and are updated as the product changes. ' +
    'The Turkish version is the reference text.',
  sections: [
    {
      key: 'terms',
      label: 'Terms of Use',
      blocks: [
        {
          title: '1. Description of the service',
          body: [
            '{brand} is player and library software. It does not sell channel, movie or series packages. ' +
            'The personal IPTV feature lets you use an M3U or Xtream source you already have in this player; ' +
            'the subscription to that source and the legality of its content are governed by your relationship ' +
            'with your provider.',
            'The app also contains two kinds of built-in content: live streams of free-to-air television ' +
            'channels and videos on YouTube. Both are fetched from their origin and relayed to you through ' +
            'our server.',
          ],
        },
        {
          title: '2. Built-in channels and videos',
          body: [
            'The built-in content section relays free-to-air television streams and YouTube videos through ' +
            'our server.',
            'There is no distribution agreement or licence with broadcasters. YouTube content is subject to ' +
            'YouTube’s own terms of service. This section may be removed without prior notice, either upon ' +
            'notification from rights holders or by our own decision; its removal does not affect the other ' +
            'features of the service.',
            'Paid plans provide player features, not content: removal of the daily time limit on YouTube ' +
            'videos and, on Plus, the ability to add your own list.',
            'Built-in channels are available with no time limit on every plan, including Free. No plan fee ' +
            'is charged in return for these channels.',
          ],
        },
        {
          title: '3. Account',
          body: [
            'Creating an account requires a valid e-mail address and a password of at least 8 characters. ' +
            'If you share your account with third parties, you are responsible for any use arising from that.',
            'You can delete your account at any time from the Settings screen. Deletion also removes your ' +
            'sources, favourites, watch history and device records; copies in backups are deleted ' +
            'within 14 days at the latest.',
            'You must be at least 18 years old to use the service. Persons under 18 may not create an account.',
          ],
        },
        {
          title: '4. One stream at a time',
          body: [
            'Only one stream can be open per account at any time. This limit is the same on every plan and ' +
            'cannot be raised; it is part of how the product works.',
            'When you start a stream on another device, the new stream opens after the previous one has ' +
            'been stopped. Two streams never run at the same time.',
          ],
        },
        {
          title: '5. Free plan limit',
          body: [
            'The Free plan includes a total of 30 minutes of YouTube playback per day. Built-in channels do ' +
            'not count towards it. This allowance is tracked ' +
            'account-wide: refreshing the page, switching devices or signing in again does not reset it. ' +
            'The counter resets every day at midnight (00:00), Türkiye time.',
            'When the allowance runs out, playback stops and your position is saved.',
          ],
        },
        {
          title: '6. Unacceptable use',
          body: [
            'The following may lead to suspension of the account:',
            { list: [
              'Using the service to reach content you are not entitled to access.',
              'Technical attempts to circumvent the single-stream limit or the daily time limit.',
              'Sending automated or excessive requests to the server.',
              'Attempting to access other users’ accounts.',
            ] },
          ],
        },
        {
          title: '7. Availability and liability',
          body: [
            'Interruptions, data loss or feature changes may occur. No specific uptime commitment is given.',
            'Device compatibility is not guaranteed; the supported browsers and devices are listed on the ' +
            'Compatibility page.',
            'The service is provided “as is”. No liability is accepted for indirect damages arising from ' +
            'interruption or data loss; cases of intent and gross negligence are reserved.',
          ],
        },
        {
          title: '8. Suspension of the account',
          body: [
            'If use contrary to the rules above is detected, the account may be suspended. In that case you ' +
            'can write to the address in the Contact section to learn the reason and to object. ' +
            'Objections are reviewed within thirty days.',
          ],
        },
        {
          title: '9. Changes to these terms',
          body: [
            'These texts are updated as the product evolves. For a significant change, the “last updated” ' +
            'date at the top of the page changes. Notifications ' +
            'are not sent by e-mail; you need to follow changes on this page. Continuing to use the service after a change means you accept the new terms.',
          ],
        },
        {
          title: '10. Governing law',
          body: [
            'These terms are governed by the law of the Republic of Türkiye. Consumer arbitration committees ' +
            'and consumer courts have jurisdiction over disputes.',
          ],
        },
      ],
    },
    {
      key: 'privacy',
      label: 'Privacy Policy',
      blocks: [
        {
          title: '1. Data collected',
          body: [
            'Only the data needed to run and measure the service and to detect problems is collected:',
            { list: [
              'Account: your e-mail address, a hash of your password (the password itself is never stored) and the name you entered, if any.',
              'Device: a name derived from the device type reported by your browser (such as “Phone” or “Car display”) and the last-seen time.',
              'Session: the session cookie kept in your browser; the server stores only a hash of this token.',
              'Source: the M3U/Xtream address, username and password you add. Stored encrypted (AES-256-GCM) and never written to logs.',
              'Viewing: what you watched, where you left off, your favourites and the total time used per day.',
              'Usage log: the time and device type of your actions in the app (sign-in, screens opened, searches and search text, playback start and end, selected quality, pause, seeking, stalls, full screen, favourites, adding/removing sources, plan operations). The IP address is not written to this log. It is kept to measure use of the service and to detect problems.',
              'Connection quality measurement: if playback stalls, technical values reported by your browser are recorded so that the cause (your connection or the source) can be told apart: download speed, latency, connection type (for example 4g), buffer length and stall count. These values are written to the usage log and the application logs together with the content being watched and the device type; they contain no location data or IP address.',
              'Payment: no card details are requested or stored by this service.',
            ] },
          ],
        },
        {
          title: '2. Password and source credentials',
          body: [
            'Your account password is hashed with scrypt; it cannot be reversed and cannot be viewed by us. ' +
            'Your source username and password are stored encrypted with AES-256-GCM and decrypted only when a ' +
            'stream is opened. Addresses and credentials are masked in error logs; this behaviour is protected ' +
            'by automated tests.',
          ],
        },
        {
          title: '3. Data sent to third parties',
          body: [
            'No third-party analytics tool, advertising tracker or social media pixel is used. The only cookie ' +
            'is the session cookie; there are no advertising cookies.',
            'The following transfers exist out of technical necessity:',
            { list: [
              'Video thumbnails are loaded directly from Google’s server (i.ytimg.com); your browser’s IP address reaches Google.',
              'If you have added your own IPTV list, channel and poster logos are loaded directly from your provider; your browser’s IP address reaches that provider. Video and audio are handled differently (see below).',
              'When you search, the search text is forwarded to the provider through our server; your IP address is not forwarded.',
              'Video and audio data flow through our server. In this flow the content provider sees our server’s address, not yours.',
              'When you request a password reset, and for transaction notices about your subscription (purchase, charge, failed payment, cancellation and trial-ending reminder), your e-mail address and the content of the message are passed to the e-mail service provider (Resend, USA) for delivery. No other e-mail is sent to you; no promotional messages are sent.',
            ] },
          ],
        },
        {
          title: '4. Retention periods',
          body: [
            { list: [
              'Account and source details: for as long as your account is open.',
              'Session tokens: 30 days; invalidated immediately when you sign out or remove the device. Dead records are deleted after 30 days.',
              'Daily usage counter: kept for quota calculation, deleted after 90 days.',
              'Device records: devices with no remaining session that have not been seen for 180 days are deleted.',
              'Stream session history (time, device, title, amount of data transferred): 90 days, then deleted.',
              'Usage log (action log): 2 years. When you delete your account the records lose their link to it and to your device, search text is deleted, and the records remain only as anonymous aggregate statistics.',
              'Rate-limit counters: tied to the IP address, not matched to an identity, deleted after 24 hours.',
              'Password reset links: valid for 60 minutes; deleted immediately when used, otherwise within 24 hours of expiry at the latest.',
              'Programme guide (EPG) cache: 7 days.',
              'Payment records (transaction type, plan, amount, date, transaction and subscription numbers at the payment provider): with your account while it is open; when you delete your account, detached from it for 10 years, then deleted.',
              'Application logs: at most 30 days and 500 MB. They contain no passwords or source credentials.',
              'Web server access log: the requested address, time, IP address and browser information are kept for 14 days. An IP address is personal data under KVKK; this log is kept to detect abuse.',
              'Backups: a daily backup of the database is taken and 14 copies are retained.',
            ] },
          ],
        },
        {
          title: '5. Exercising your rights',
          body: [
            'You can delete your account yourself from the Settings screen; once you confirm your password, ' +
            'your account and all linked records are deleted from the database.',
            'There are three exceptions. The first is backups: because a daily backup of the database is taken, ' +
            'a copy of a deleted account remains in backups for up to 14 days and is then deleted.',
            'The second is the trial record: if an account that has used the trial period is deleted, an ' +
            'irreversible digest of its e-mail address is kept. The address itself is not stored; the record ' +
            'is used only so that an account reopened with the same address is not given the trial a second time.',
            'The third is payment records: if you made transactions through the payment provider, the type, ' +
            'plan, amount and date of each transaction and the transaction and subscription numbers at the ' +
            'payment provider are kept as commercial records for 10 years and then deleted. These records ' +
            'are kept detached from your account; your e-mail address, name and device details are not in them.',
            'To request a copy of your data or ask for a correction, write to the address in the Contact section.',
          ],
        },
      ],
    },
    {
      key: 'cookies',
      label: 'Cookie Policy',
      blocks: [
        {
          title: '1. Cookies used',
          body: [
            'Only one cookie is used: the session cookie. No advertising, measurement or third-party cookies ' +
            'are written.',
            'Since there is no non-essential cookie that would require consent, no “accept cookies” banner is ' +
            'shown. The information on this page describes the current implementation; its legal ' +
            'classification is subject to a lawyer’s assessment.',
          ],
        },
        {
          title: '2. Session cookie',
          body: [
            { list: [
              'Name: akis_session.',
              'Purpose: recognising you after you sign in. Without it, every request would require signing in again.',
              'Lifetime: 30 days. Invalidated immediately when you sign out or remove the device.',
              'Settings: HttpOnly (not readable by JavaScript), SameSite=Lax (not sent from other sites), Secure over HTTPS.',
              'Content: a random token. The server stores a hash of the token, not the token itself.',
            ] },
          ],
        },
        {
          title: '3. Other records kept in the browser',
          body: [
            'A few small records that are not cookies are stored only on your device. The records themselves are not kept on the server; however, your selected language and quality are sent with requests, and a search is sent to the server when you make it:',
            { list: [
              'The video quality you selected (for example 720p).',
              'Your recent searches; you can delete them with “Clear” on the search screen.',
              'Your interface language preference.',
              'Which layout you are using (phone / car / computer).',
              'Two records cleared when the tab closes: whether you have seen the driving warning in the car, and a one-time reload marker for a new build.',
            ] },
            'Clearing site data in the browser removes all of these records.',
          ],
        },
      ],
    },
    {
      key: 'kvkk',
      label: 'Privacy Notice (KVKK)',
      blocks: [
        {
          title: '1. Notice',
          body: [
            'Under Turkish Personal Data Protection Law No. 6698 (KVKK), the following explains which of your ' +
            'data is processed and for what purpose.',
            'Data controller: the developer who currently operates the service as an individual. You can reach ' +
            'them at the address in the Contact section. Once commercial operation begins, the trade name and ' +
            'address will be added to this section.',
            'Data is collected through the form you fill in when creating an account and, by automated ' +
            'means, while you use the service, in electronic form.',
          ],
        },
        {
          title: '2. Data processed and purposes',
          body: [
            { list: [
              'Identity and contact (e-mail, name if provided): creating the account and verifying sign-in.',
              'Transaction security (password hash, session token hash, device name, last-seen time): preventing unauthorised use of your account.',
              'Usage data (content watched, position, favourites, daily total time): operating the service and applying plan entitlements.',
              'Usage log (action log: time, action type, device type, content and quality, search text and connection quality measurements): measuring use of the service, detecting problems and meeting legal obligations.',
              'Source details (your IPTV address, username and password): connecting to that source only on your instruction. Stored encrypted.',
              'Short-lived IP counters: limiting abuse and automated attacks.',
            ] },
          ],
        },
        {
          title: '3. Legal basis',
          body: [
            'Account and service data are processed for the formation and performance of the contract ' +
            '(KVKK art. 5/2-c); security counters are processed on the basis of legitimate interest ' +
            '(KVKK art. 5/2-f). No processing requiring explicit consent takes place: there is no marketing ' +
            'profiling, advertising tracking or sale of data.',
          ],
        },
        {
          title: '4. Transfers and server location',
          body: [
            'Your personal data is not sold and is not transferred to third parties for marketing purposes. ' +
            'Transfers arising from technical necessity are listed individually in the Privacy Policy: video ' +
            'thumbnails come from Google’s server, and if you have added your own list, channel logos come ' +
            'directly from your provider to your browser; search text is forwarded through our server.',
            'When you request a password reset, and for transaction notices about your subscription, your e-mail ' +
            'address is transferred to the e-mail service provider (Resend, USA), which is established abroad, so ' +
            'that the message can be delivered.',
            'Your data is stored on the server that runs the service. If that server is located outside ' +
            'Türkiye, this constitutes a transfer abroad within the meaning of KVKK art. 9; this point will ' +
            'be clarified and stated here explicitly before commercial operation begins.',
          ],
        },
        {
          title: '5. Your rights',
          body: [
            'Under KVKK art. 11 you have the right to access, correct and delete your data and to object to ' +
            'its processing. You can submit a request to the address in the Contact section; requests are ' +
            'answered within thirty days at the latest. Deleting your account from the Settings screen means ' +
            'your data is deleted (the copy in backups is deleted within 14 days).',
            'In the event of a data breach, affected users and the relevant authority are notified within the ' +
            'period required by law.',
          ],
        },
      ],
    },
    {
      key: 'subscription',
      label: 'Subscription and Cancellation',
      blocks: [
        {
          title: '1. Plans and prices',
          body: [
            'There are three plans: Free (30 minutes of YouTube a day, no personal IPTV), Starter (120.00 TL/month; no ' +
            'personal IPTV) and Plus (249.00 TL/month; you can add your own M3U or Xtream list). ' +
            'Prices include VAT.',
            'Paid plans can also be bought yearly. With yearly payment you pay for 10 months and use the plan ' +
            'for 12: Starter 1,200.00 TL/year, Plus 2,490.00 TL/year. A yearly period is 365 days.',
            'An account moving to a paid plan for the first time gets a 7-day trial. You are not charged ' +
            'during the trial; if you cancel before it ends you pay nothing, otherwise the fee for the period ' +
            'you chose is charged when it ends. The trial applies only once per account. The amount and ' +
            'renewal period are shown again at the payment step.',
            'The Plus plan allows two sources (M3U or Xtream) at the same time; this number is not unlimited.',
            'Unlimited use is not promised; a fair-use policy applies and any limits are announced in advance.',
          ],
        },
        {
          title: '2. Payment',
          body: [
            'Payments are processed by iyzico. You enter your card details in iyzico’s form; card details do ' +
            'not reach this site and are not stored here. iyzico may verify your card with a 1 TL transaction, ' +
            'which is refunded.',
            'When a subscription is opened you are asked for the details iyzico requires: first name, last ' +
            'name, Turkish ID number, mobile phone and billing address. These are sent only to iyzico and are ' +
            'not stored on this site.',
            'While the payment system is in its trial (test) phase, the payment page says so clearly and no ' +
            'real charge is made. Invoicing arrangements will be added to this section before real ' +
            'charging begins; refund terms are under “Right of withdrawal and refunds” below.',
          ],
        },
        {
          title: '3. Renewal and cancellation',
          body: [
            'Subscriptions renew in 30-day (monthly) or 365-day (yearly) periods, depending on the billing period you chose. When you cancel, your access is not cut off immediately: ' +
            'all features remain available until the end of the current period, after which your account ' +
            'returns to the Free plan. On cancellation, no refund is made for the unused time of the current period.',
            'Cancellation is done on the Plans screen. A subscription opened through the payment provider cannot be ' +
            'un-cancelled; if you subscribe again before the period ends, the new subscription starts on the day you ' +
            'buy it and the remaining time of the current period is not added.',
            'You can change your plan on the Plans screen as long as the billing period stays the same. When ' +
            'you move to a higher plan, its features are available immediately and you are not charged extra ' +
            'for the current period; the new price applies from the next period. A move to a lower plan takes ' +
            'effect at the end of the current period. A change made during the trial takes effect immediately ' +
            'and the new plan’s price is charged when the trial ends. Changing the billing period (monthly or ' +
            'yearly) requires cancelling and starting the subscription again.',
            'You can change your payment card on the Plans screen through the payment provider’s form. ' +
            'Because card details are not kept on this site, your card cannot be displayed here.',
            'When you leave the Plus plan, your personal IPTV access closes; the sources and catalogue records ' +
            'you added are not deleted, and access reopens when you return to Plus.',
          ],
        },
        {
          title: '4. Right of withdrawal and refunds',
          body: [
            'The service starts to be provided electronically as soon as the purchase is completed. At the ' +
            'payment step you confirm that you want the service to start immediately and that you therefore ' +
            'have no right of withdrawal; services performed instantly in an electronic environment are among ' +
            'the exceptions to the right of withdrawal in the Turkish Regulation on Distance Contracts.',
            'The 7-day trial period granted the first time you move to a paid plan is there for you to ' +
            'evaluate the service without paying: if you cancel within it, you are not charged.',
            'Amounts charged are not refunded. When you cancel your subscription, no charge is made for a ' +
            'new period and you keep using the service until the end of the current period.',
            'If you believe a charge was made in error or twice, write to the address in the Contact section; ' +
            'an amount found to have been taken wrongly after review is refunded.',
            'In the event of a dispute you may apply to the Consumer Arbitration Committee or the Consumer ' +
            'Court at your place of residence, within the monetary limits announced each year by the Turkish ' +
            'Ministry of Trade.',
          ],
        },
      ],
    },
    {
      key: 'safety',
      label: 'In-Car Safety',
      blocks: [
        {
          title: '1. No video while driving',
          body: [
            'This product is designed to run on car displays as well. The basic rule is: the driver does not ' +
            'watch video while the vehicle is moving.',
            'Watching video is distracting and may be illegal in your country. A driver looking at the screen ' +
            'in a moving vehicle is in breach of traffic law, and the resulting liability rests with the driver.',
          ],
        },
        {
          title: '2. Measures taken',
          body: [
            { list: [
              'Tasks that require long text entry on the car display (adding a source) are handed off to the phone.',
              'In the car layout, touch targets are enlarged and text is made larger so that glance time is shorter.',
              'Nothing is done to circumvent the vehicle’s restrictions on video while driving. Full screen is opened only through the Theatre mode the vehicle offers while parked, and only with your confirmation.',
            ] },
          ],
        },
        {
          title: '3. Technical limits',
          body: [
            'Whether the vehicle is moving cannot be determined from the browser; this information is not ' +
            'made available to web pages. Playback therefore cannot be stopped automatically. If the car’s own ' +
            'system enforces this restriction, it is not interfered with.',
            'Responsibility rests with the driver. The app is designed for passengers.',
          ],
        },
      ],
    },
    {
      key: 'copyright',
      label: 'Copyright',
      blocks: [
        {
          title: '1. Where content comes from',
          body: [
            'No content is stored on our servers; content is only relayed during playback. There are three ' +
            'separate sources, each with a different status:',
            { list: [
              'Your own IPTV list: comes from the M3U/Xtream source you added. The legality of that source is your responsibility.',
              'Built-in live channels: taken from the free-to-air streams of television channels and relayed through our server. There is no distribution agreement with broadcasters.',
              'Discover and search: videos on YouTube, likewise relayed through our server.',
            ] },
            'The last two items are removed upon notification from rights holders. Watching content you are not entitled to access through this ' +
            'service is a breach of the Terms of Use.',
          ],
        },
        {
          title: '2. Notices from rights holders',
          body: [
            'If you believe a user is infringing your rights through the service, you can write to the address ' +
            'in the Contact section with the following information:',
            { list: [
              'Information showing that you are the rights holder.',
              'A description of the work concerned.',
              'Information on where the infringement occurred (account, date, content title).',
              'Your contact details and a statement on the accuracy of your declaration.',
            ] },
            'Notices are reviewed. For a notice concerning built-in channels or videos in the Discover ' +
            'section, the relevant stream is removed without delay. For user sources, the relevant source is ' +
            'removed and the account is suspended on repeated infringement. Obligations under Turkish Law ' +
            'No. 5651 will be addressed separately once commercial operation begins.',
          ],
        },
        {
          title: '3. Open-source components',
          body: [
            'The interface typeface is Manrope (SIL Open Font License 1.1). The playback path uses mp4box.js, ' +
            'ffmpeg and yt-dlp, each under its own licence. The illustrations on the landing page were ' +
            'produced for this project and are not actual catalogue images.',
          ],
        },
      ],
    },
    {
      key: 'contact',
      label: 'Contact',
      blocks: [
        {
          title: '1. Get in touch',
          body: [
            'For support, privacy requests, copyright notices and all other questions:',
            { contact: 'iletisim@akisplay.com' },
            'Requests are answered within thirty days at the latest.',
          ],
        },
        {
          title: '2. Service operator',
          body: [
            'This service is operated by an individual and no online payments are taken. Once online sales ' +
            'begin, the trade name, address, tax number and ETBİS registration details will be added to this ' +
            'section; the distance sales contract and the preliminary information form will be published at ' +
            'that stage.',
          ],
        },
      ],
    },
  ],
};
